import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  fr: {
    // Navigation
    home: "Accueil",
    formation: "Formation",
    pme: "PME",
    freelance: "Freelance",
    employes: "Employés de bureau",
    testimonials: "Témoignages",
    appointment: "Rendez-vous",
    admin: "Admin",
    accounting: "Comptabilité",
    
    // Hero
    slogan: "IA à vos côtés, temps retrouvé, argent optimisé, liberté amplifiée",
    heroTitle: "Transformez votre business avec l'Intelligence Artificielle",
    heroSubtitle: "Découvrez nos agents IA personnalisés pour automatiser vos tâches et booster votre productivité",
    getStarted: "Commencer",
    learnMore: "En savoir plus",
    
    // Branches
    branchFormation: "Formation",
    branchFormationDesc: "Agents IA pour automatiser la formation et le suivi des apprenants",
    branchPME: "PME",
    branchPMEDesc: "Solutions IA complètes pour les petites et moyennes entreprises",
    branchFreelance: "Freelance",
    branchFreelanceDesc: "Assistants IA pour gérer votre activité indépendante",
    branchEmployes: "Employés de bureau",
    branchEmployesDesc: "Automatisez vos tâches administratives quotidiennes",
    
    // Forms
    name: "Nom",
    email: "Email",
    phone: "Téléphone",
    message: "Message",
    needs: "Décrivez vos besoins",
    submit: "Envoyer",
    cancel: "Annuler",
    save: "Sauvegarder",
    clear: "Effacer",
    download: "Télécharger",
    
    // Voice
    voiceRecord: "Enregistrer vocalement",
    voiceStop: "Arrêter",
    voiceTranscript: "Transcription vocale",
    
    // Quote & Contract
    generateQuote: "Générer un devis",
    acceptQuote: "Accepter le devis",
    viewContract: "Voir le contrat",
    signContract: "Signer le contrat",
    downloadContract: "Télécharger le contrat",
    signature: "Signature",
    signHere: "Signez ici",
    
    // Thank you
    thankYou: "Merci pour votre confiance !",
    thankYouMessage: "Nous avons bien reçu votre demande. Notre équipe vous contactera dans les plus brefs délais.",
    
    // Footer
    newsletter: "Newsletter",
    subscribeNewsletter: "Inscrivez-vous à notre newsletter",
    emailPlaceholder: "Votre email",
    subscribe: "S'abonner",
    followUs: "Suivez-nous",
    allRightsReserved: "Tous droits réservés",
    
    // Admin
    dashboard: "Tableau de bord",
    clients: "Clients",
    quotes: "Devis",
    contracts: "Contrats",
    content: "Contenu",
    manageContent: "Gérer le contenu",
    
    // Misc
    loading: "Chargement...",
    error: "Erreur",
    success: "Succès",
    selectCountry: "Indicatif pays",
    preferredDate: "Date préférée",
    preferredTime: "Heure préférée",
    bookAppointment: "Réserver un rendez-vous WhatsApp",
    whatsappContact: "Contact WhatsApp",
    price: "Prix",
    features: "Fonctionnalités",
    demoVideo: "Vidéo démo",
    viewDemo: "Voir la démo",
    addTestimonial: "Ajouter un témoignage",
    yourRating: "Votre note",
    uploadMedia: "Ajouter photo/vidéo",
  },
  en: {
    // Navigation
    home: "Home",
    formation: "Training",
    pme: "SME",
    freelance: "Freelance",
    employes: "Office Workers",
    testimonials: "Testimonials",
    appointment: "Appointment",
    admin: "Admin",
    accounting: "Accounting",
    
    // Hero
    slogan: "AI by your side, time regained, money optimized, freedom amplified",
    heroTitle: "Transform your business with Artificial Intelligence",
    heroSubtitle: "Discover our customized AI agents to automate your tasks and boost your productivity",
    getStarted: "Get Started",
    learnMore: "Learn More",
    
    // Branches
    branchFormation: "Training",
    branchFormationDesc: "AI agents to automate training and learner tracking",
    branchPME: "SME",
    branchPMEDesc: "Complete AI solutions for small and medium businesses",
    branchFreelance: "Freelance",
    branchFreelanceDesc: "AI assistants to manage your independent activity",
    branchEmployes: "Office Workers",
    branchEmployesDesc: "Automate your daily administrative tasks",
    
    // Forms
    name: "Name",
    email: "Email",
    phone: "Phone",
    message: "Message",
    needs: "Describe your needs",
    submit: "Submit",
    cancel: "Cancel",
    save: "Save",
    clear: "Clear",
    download: "Download",
    
    // Voice
    voiceRecord: "Voice record",
    voiceStop: "Stop",
    voiceTranscript: "Voice transcript",
    
    // Quote & Contract
    generateQuote: "Generate quote",
    acceptQuote: "Accept quote",
    viewContract: "View contract",
    signContract: "Sign contract",
    downloadContract: "Download contract",
    signature: "Signature",
    signHere: "Sign here",
    
    // Thank you
    thankYou: "Thank you for your trust!",
    thankYouMessage: "We have received your request. Our team will contact you shortly.",
    
    // Footer
    newsletter: "Newsletter",
    subscribeNewsletter: "Subscribe to our newsletter",
    emailPlaceholder: "Your email",
    subscribe: "Subscribe",
    followUs: "Follow us",
    allRightsReserved: "All rights reserved",
    
    // Admin
    dashboard: "Dashboard",
    clients: "Clients",
    quotes: "Quotes",
    contracts: "Contracts",
    content: "Content",
    manageContent: "Manage content",
    
    // Misc
    loading: "Loading...",
    error: "Error",
    success: "Success",
    selectCountry: "Country code",
    preferredDate: "Preferred date",
    preferredTime: "Preferred time",
    bookAppointment: "Book WhatsApp appointment",
    whatsappContact: "WhatsApp Contact",
    price: "Price",
    features: "Features",
    demoVideo: "Demo video",
    viewDemo: "View demo",
    addTestimonial: "Add testimonial",
    yourRating: "Your rating",
    uploadMedia: "Add photo/video",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    const savedLang = localStorage.getItem('tannou-language');
    if (savedLang && (savedLang === 'fr' || savedLang === 'en')) {
      setLanguage(savedLang);
    }
  }, []);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('tannou-language', lang);
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
